import React, { useEffect, useId, useRef } from "react";

const SIZES = { sm: "26rem", md: "34rem", lg: "44rem" };

/**
 * The shared modal for the whole application.
 *
 * Replaces the hand-rolled `.modal` markup, which collided with DaisyUI's
 * component of the same name, trapped no focus and could not be dismissed
 * with the keyboard.
 *
 * Handles: Escape to close, backdrop click to close, focus moved in on open
 * and returned to the trigger on close, focus cycling inside the dialog, and
 * background scroll lock.
 */
export default function Dialog({
  open,
  onClose,
  title,
  children,
  size = "md",
  tone = "default",
}) {
  const panelRef = useRef(null);
  const restoreRef = useRef(null);
  const titleId = useId();

  // Held in a ref so the effect below can call the latest onClose without
  // listing it as a dependency. Parents pass an inline arrow or a function
  // redeclared each render, so depending on it re-ran the whole effect on
  // every keystroke: the cleanup restored focus to the element that opened
  // the dialog, which is why only the first character of any field landed.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    restoreRef.current = document.activeElement;

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    // Move focus into the dialog so a keyboard user is not left behind it.
    const timer = window.setTimeout(() => {
      const focusable = panelRef.current?.querySelector(
        'input:not([type="hidden"]), select, textarea, button, [href], [tabindex]:not([tabindex="-1"])'
      );
      (focusable || panelRef.current)?.focus();
    }, 0);

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      if (e.key !== "Tab") return;

      const nodes = panelRef.current?.querySelectorAll(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!nodes || nodes.length === 0) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = overflow;
      // Return focus to whatever opened the dialog.
      if (restoreRef.current instanceof HTMLElement) restoreRef.current.focus();
    };
    // Only `open` belongs here. Adding onClose made this teardown run on every
    // parent render, stealing focus out of whatever field was being typed in.
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="es-dialog__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="es-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ maxWidth: SIZES[size] || SIZES.md }}
        ref={panelRef}
        tabIndex={-1}
      >
        <header className="es-dialog__head">
          <h2
            className="es-dialog__title"
            id={titleId}
            style={tone === "danger" ? { color: "var(--es-bad)" } : undefined}
          >
            {title}
          </h2>
          <button
            type="button"
            className="es-dialog__close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </header>

        <div className="es-dialog__body">{children}</div>
      </div>
    </div>
  );
}
